import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, unitImages } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import { useUploadedImages } from "../../hooks/useUploadedImages";
import { Dialog, DialogContent } from "../ui/dialog";

export default function Units() {
  const { t, lang } = useLang();
  const [units, setUnits] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const { getUnitImages } = useUploadedImages();

  useEffect(() => {
    api.get("/units").then(({ data }) => setUnits(data.units || [])).catch(() => {});
  }, []);

  const open = (uid, idx) => setLightbox({ unitId: uid, idx });
  const close = () => setLightbox(null);
  const getLightboxImg = () => {
    if (!lightbox) return null;
    const img = unitImages[lightbox.unitId] || {};
    const defaultGallery = img.gallery || [img.hero, img.interior, img.detail].filter(Boolean);
    const uploaded = getUnitImages(lightbox.unitId);
    const gallery = uploaded.length > 0 ? [...uploaded, ...defaultGallery] : defaultGallery;
    return gallery[lightbox.idx] || null;
  };
  const lightImg = getLightboxImg();

  const single = units.filter((u) => !u.combo_member_ids);
  const combo = units.find((u) => u.combo_member_ids);

  return (
    <section id="units" data-testid="units-section" className="section-pad bg-[hsl(var(--sage))] border-t border-[hsl(var(--foreground))]/8">
      <div className="container-x">
        <div className="max-w-2xl mb-14">
          <div className="overline mb-5">{lang === "de" ? "Unsere Unterkünfte" : "Our stays"}</div>
          <h2 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight text-[hsl(var(--foreground))]">
            {lang === "de" ? "Zwei Häuser.\nEin Gefühl." : "Two houses.\nOne feeling."}
          </h2>
          <p className="mt-6 text-base md:text-lg text-[hsl(var(--foreground))]/85">
            {lang === "de"
              ? "Wähle dein Refugium — das Zirben Chalet mit warmem Naturholz oder das klassische Landhaus mit alpinem Charakter. Für größere Gruppen: beide zusammen."
              : "Pick your retreat — the Zirben Chalet with warm pine wood, or the classic Landhaus with alpine soul. For larger groups: both together."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14">
          {single.map((u) => {
            const img = unitImages[u.id] || {};
            const defaultGallery = img.gallery || [img.hero, img.interior, img.detail].filter(Boolean);
            const uploaded = getUnitImages(u.id);
            const gallery = uploaded.length > 0 ? [...uploaded, ...defaultGallery] : defaultGallery;
            const heroImg = uploaded.length > 0 ? uploaded[0] : img.hero;
            const tagline = lang === "de" ? u.tagline_de : u.tagline_en;
            const description = lang === "de" ? u.description_de : u.description_en;
            return (
              <article key={u.id} data-testid={`unit-card-${u.id}`} className="house-card group border border-[hsl(var(--foreground))]/10">
                <button type="button" onClick={() => open(u.id, 0)} className="block w-full aspect-[4/3] overflow-hidden bg-[hsl(var(--muted))] relative" aria-label={u.name}>
                  <img src={heroImg} alt={u.name} className="w-full h-full object-cover gallery-img" loading="lazy" />
                  <span className="absolute bottom-4 left-4 bg-[hsl(var(--primary))]/90 backdrop-blur-sm px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase font-medium text-[hsl(var(--primary-foreground))]">
                    {gallery.length} {lang === "de" ? "Fotos" : "photos"}
                  </span>
                </button>

                <div className="grid grid-cols-3 gap-2 md:gap-3 mt-2 md:mt-3 px-2 md:px-3">
                  {gallery.slice(1, 4).map((src, i) => (
                    <button key={src} type="button" onClick={() => open(u.id, i + 1)} data-testid={`unit-${u.id}-thumb-${i}`} className="aspect-[4/3] overflow-hidden bg-[hsl(var(--card))]">
                      <img src={src} alt={`${u.name} ${i + 1}`} className="w-full h-full object-cover gallery-img" loading="lazy" />
                    </button>
                  ))}
                </div>

                <div className="p-8 md:p-10">
                  <div className="flex items-center gap-4 mb-4 flex-wrap">
                    <div className="inline-flex items-center gap-1.5 bg-[hsl(var(--accent))]/15 border border-[hsl(var(--accent))]/40 px-2.5 py-1">
                      <svg className="w-3.5 h-3.5 text-[hsl(var(--accent))]" viewBox="0 0 20 20" fill="currentColor" aria-hidden><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.447a1 1 0 00-.363 1.118l1.287 3.957c.3.922-.755 1.688-1.54 1.118l-3.371-2.446a1 1 0 00-1.175 0l-3.37 2.446c-.784.57-1.838-.196-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.05 9.384c-.783-.57-.38-1.81.588-1.81h4.16a1 1 0 00.951-.69l1.286-3.957z"/></svg>
                      <span className="text-[11px] font-bold text-[hsl(var(--accent))] tracking-wide">4.9</span>
                      <span className="text-[10px] text-[hsl(var(--foreground))]/78">· 47</span>
                    </div>
                    <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase font-semibold text-[hsl(var(--foreground))]/85">
                      <svg className="w-3 h-3 text-[hsl(var(--accent))]" viewBox="0 0 20 20" fill="currentColor" aria-hidden><path d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"/></svg>
                      {lang === "de" ? "Sofort buchbar" : "Instant book"}
                    </div>
                  </div>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <h3 className="font-serif text-3xl md:text-4xl text-[hsl(var(--foreground))]">{u.name}</h3>
                    <div className="text-right shrink-0">
                      <div className="overline">{lang === "de" ? "Pro Nacht" : "Per night"}</div>
                      <div className="mt-1 font-serif text-2xl text-[hsl(var(--accent))] leading-none">
                        €{u.summer_rate_eur.toFixed(0)} <span className="text-[hsl(var(--foreground))]/72 text-base">/</span> €{u.winter_rate_eur.toFixed(0)}
                      </div>
                      <div className="mt-1 text-[10px] tracking-[0.15em] uppercase text-[hsl(var(--foreground))]/88">{lang === "de" ? "Sommer / Winter" : "Summer / Winter"}</div>
                    </div>
                  </div>
                  <p className="mt-4 text-[hsl(var(--foreground))]/85 italic font-serif text-lg leading-snug">{tagline}</p>
                  {description && (
                    <p className="mt-4 text-sm text-[hsl(var(--foreground))]/80 leading-relaxed">{description}</p>
                  )}

                  {u.owner && (
                    <div className="mt-4 text-xs text-[hsl(var(--foreground))]/88">
                      <span className="overline !text-[hsl(var(--foreground))]/88">{lang === "de" ? "Vermieter" : "Host"}</span>
                      <span data-testid={`unit-owner-${u.id}`} className="ml-2 text-[hsl(var(--foreground))]/85">{u.owner}</span>
                    </div>
                  )}

                  <div className="mt-8 grid grid-cols-3 gap-4 border-t border-[hsl(var(--foreground))]/12 pt-6">
                    <Stat label={lang === "de" ? "Gäste" : "Guests"} value={u.max_guests} />
                    <Stat label={lang === "de" ? "Schlafzimmer" : "Bedrooms"} value={u.bedrooms} />
                    <Stat label="m²" value={u.living_area_m2} />
                  </div>

                  <div className="mt-8">
                    <Link to={`/book?unit=${u.id}`} data-testid={`unit-book-${u.id}`} className="btn-primary">
                      {t.nav.book} {u.name} <span aria-hidden>→</span>
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Combo highlight — full-width below */}
        {combo && (
          <article data-testid="unit-card-combo" className="mt-10 house-card !bg-gradient-to-br !from-[hsl(var(--cream))] !to-[hsl(var(--sage))] text-[hsl(var(--foreground))] border border-[hsl(var(--accent))]/40 grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-6 relative overflow-hidden bg-[hsl(var(--muted))] min-h-[280px]">
              <img src={unitImages.combo?.hero} alt="Combo" className="absolute inset-0 w-full h-full object-cover gallery-img" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[hsl(var(--foreground))]/15" />
              <span className="absolute top-5 left-5 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-3 py-1.5 text-[11px] tracking-[0.22em] uppercase font-semibold">
                {lang === "de" ? "10 % Combo-Rabatt" : "10 % Combo discount"}
              </span>
            </div>
            <div className="lg:col-span-6 p-8 md:p-12 lg:p-14 flex flex-col">
              <div className="overline !text-[hsl(var(--accent))]">{lang === "de" ? "Combo · Gruppenbuchung" : "Combo · group booking"}</div>
              <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl mt-3 leading-[1.05]">
                {lang === "de" ? "Beide Häuser zusammen." : "Both houses together."}
              </h3>
              <p className="mt-4 text-[hsl(var(--foreground))]/80 max-w-md leading-relaxed">
                {lang === "de"
                  ? "Für Familienfeste, Hochzeiten oder Freundes-Wochenenden: Zirben Chalet + Landhaus mit 10 % Rabatt — bis zu 20 Gäste, 9 Schlafzimmer, 325 m²."
                  : "For family reunions, weddings or weekends with friends: Zirben Chalet + Landhaus with 10 % off — up to 20 guests, 9 bedrooms, 325 m²."}
              </p>

              <div className="mt-8 grid grid-cols-4 gap-4 border-t border-[hsl(var(--foreground))]/15 pt-6">
                <ComboStat label={lang === "de" ? "Gäste" : "Guests"} value={combo.max_guests} />
                <ComboStat label={lang === "de" ? "Schlafzi." : "Bedrooms"} value={combo.bedrooms} />
                <ComboStat label={lang === "de" ? "Bäder" : "Baths"} value={combo.bathrooms} />
                <ComboStat label="m²" value={combo.living_area_m2} />
              </div>

              <div className="mt-8 flex flex-wrap items-end gap-6 justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--accent))]">{lang === "de" ? "Pro Nacht" : "Per night"}</div>
                  <div className="font-serif text-3xl mt-1">
                    €{combo.summer_rate_eur.toFixed(0)} <span className="text-[hsl(var(--foreground))]/78 text-xl">/</span> €{combo.winter_rate_eur.toFixed(0)}
                  </div>
                  <div className="text-[10px] tracking-[0.15em] uppercase text-[hsl(var(--foreground))]/78 mt-1">{lang === "de" ? "Sommer / Winter" : "Summer / Winter"}</div>
                </div>
                <Link to="/book?unit=combo" data-testid="unit-book-combo" className="btn-primary">
                  {lang === "de" ? "Combo buchen" : "Book Combo"} <span aria-hidden>→</span>
                </Link>
              </div>
            </div>
          </article>
        )}
      </div>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-5xl !rounded-none p-0 border-none bg-black">
          {lightImg && <img src={lightImg} alt="" className="w-full h-auto object-contain" />}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-serif text-2xl text-[hsl(var(--accent))]">{value}</div>
      <div className="text-[10px] tracking-[0.15em] uppercase text-[hsl(var(--foreground))]/88 mt-1">{label}</div>
    </div>
  );
}

function ComboStat({ label, value }) {
  return (
    <div>
      <div className="font-serif text-2xl">{value}</div>
      <div className="text-[10px] tracking-[0.15em] uppercase text-[hsl(var(--foreground))]/78 mt-1">{label}</div>
    </div>
  );
}
